import { AppState } from 'react-native';
import { apiClient } from '../../shared/api-client';
/**
 * Client telemetry leaves in batches (2026-09-24).
 *
 * Every event used to be its own POST: a two-person call sent about a hundred
 * requests, each carrying more header than body. Events now wait here and go
 * as `{ events: [...] }`, one request per batch:
 *
 * - routine samples (media quality, handoff proximity) wait up to
 *   TELEMETRY_ROUTINE_FLUSH_MS, so a call's samples share one request;
 * - everything else waits TELEMETRY_URGENT_FLUSH_MS, which folds a join's
 *   burst of stage events into one or two requests without delaying the
 *   admin join timeline noticeably;
 * - a full batch, a hidden page or a backgrounded app flushes at once.
 *
 * Each event is sent with `ageMs`, its time in this queue, and the server
 * backdates the row by it: join-stage metrics measure the gaps between rows.
 *
 * Media samples are also the server's room heartbeat (a call is ended after
 * 3 minutes without one). Sampled at most 60 s apart and flushed within 30 s,
 * one arrives at least every ~90 s; a hidden page, where timers slow down,
 * flushes samples at once.
 *
 * A server from before batching answers the batch with 400; the queue then
 * sends one event per request for the rest of this runtime.
 */
export const TELEMETRY_BATCH_MAX_EVENTS = 20;
export const TELEMETRY_URGENT_FLUSH_MS = 2000;
export const TELEMETRY_ROUTINE_FLUSH_MS = 30000;
const TELEMETRY_QUEUE_MAX = 200;
const TELEMETRY_MAX_ATTEMPTS = 2;
const TELEMETRY_PATH = '/api/telemetry/events';
let queue = [];
let timer;
let timerDueAt = 0;
let batchUnsupported = false;
let lifecycleInstalled = false;
function pageHidden() {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}
function installLifecycleFlush() {
    if (lifecycleInstalled) {
        return;
    }
    lifecycleInstalled = true;
    const flushNow = () => {
        void flushTelemetryQueue({ keepalive: true });
    };
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        AppState?.addEventListener?.('change', state => {
            if (state !== 'active') {
                flushNow();
            }
        });
        return;
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('pagehide', flushNow);
    }
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
        document.addEventListener('visibilitychange', () => {
            if (pageHidden()) {
                flushNow();
            }
        });
    }
}
function clearTimer() {
    if (timer) {
        clearTimeout(timer);
    }
    timer = undefined;
    timerDueAt = 0;
}
function scheduleFlush(delayMs) {
    const dueAt = Date.now() + delayMs;
    // An earlier flush already covers this event.
    if (timer && timerDueAt <= dueAt) {
        return;
    }
    clearTimer();
    timerDueAt = dueAt;
    timer = setTimeout(() => {
        timer = undefined;
        timerDueAt = 0;
        void flushTelemetryQueue();
    }, delayMs);
}
/**
 * Queue one telemetry event (the same body `/api/telemetry/events` accepts on
 * its own). `routine` marks periodic samples that may wait for company.
 */
export function queueTelemetryEvent(body, { routine = false } = {}) {
    installLifecycleFlush();
    queue.push({ attempts: 0,
        body,
        queuedAt: Date.now() });
    if (queue.length > TELEMETRY_QUEUE_MAX) {
        queue.splice(0, queue.length - TELEMETRY_QUEUE_MAX);
    }
    if (queue.length >= TELEMETRY_BATCH_MAX_EVENTS) {
        void flushTelemetryQueue();
        return;
    }
    scheduleFlush(routine && !pageHidden() ? TELEMETRY_ROUTINE_FLUSH_MS : TELEMETRY_URGENT_FLUSH_MS);
}
async function postSingly(events, keepalive) {
    await Promise.all(events.map(event => apiClient.post(TELEMETRY_PATH, event, { keepalive })));
}
/** True when the batch is settled (delivered, or not worth retrying). */
async function sendBatch(batch, keepalive) {
    const now = Date.now();
    const events = batch.map(item => {
        return { ...item.body,
            ageMs: Math.max(0, now - item.queuedAt) };
    });
    if (batchUnsupported) {
        await postSingly(events, keepalive);
        return true;
    }
    const response = await apiClient.post(TELEMETRY_PATH, { events }, { keepalive });
    if (response.status === 400) {
        // A server from before batching reads { events } as one malformed event.
        batchUnsupported = true;
        await postSingly(events, keepalive);
        return true;
    }
    // Offline, rate-limited or a server fault: worth another try later.
    return !(response.status === 0 || response.status === 429 || response.status >= 500);
}
function requeue(batch) {
    const retry = batch
        .map(item => {
        return { ...item,
            attempts: item.attempts + 1 };
    })
        .filter(item => item.attempts < TELEMETRY_MAX_ATTEMPTS);
    queue = [...retry, ...queue].slice(-TELEMETRY_QUEUE_MAX);
}
/**
 * Send everything queued. `keepalive` is for a page on its way out: every
 * batch leaves at once and may outlive the page.
 */
export async function flushTelemetryQueue({ keepalive = false } = {}) {
    clearTimer();
    if (keepalive) {
        const batches = [];
        while (queue.length) {
            batches.push(queue.splice(0, TELEMETRY_BATCH_MAX_EVENTS));
        }
        await Promise.all(batches.map(batch => sendBatch(batch, true).catch(() => true)));
        return;
    }
    while (queue.length) {
        const batch = queue.splice(0, TELEMETRY_BATCH_MAX_EVENTS);
        const settled = await sendBatch(batch, false).catch(() => false);
        if (!settled) {
            requeue(batch);
            if (queue.length) {
                scheduleFlush(TELEMETRY_ROUTINE_FLUSH_MS);
            }
            return;
        }
    }
}
/** Test seam: forget queued events and runtime state. */
export function resetTelemetryQueueForTests() {
    clearTimer();
    queue = [];
    batchUnsupported = false;
}
